import { useEffect, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../../../lib/api'
import { formatCents } from '../../../lib/money'
import type { FinancialKpisResponse } from '@jr2os/shared'
import { LegendDot } from './LegendDot'

interface YearRow {
  year: number
  ing: number
  ebitda: number
  net: number
  at: number
  pn: number
}

/**
 * Evolución de los últimos ejercicios, la tercera pestaña de Ledgeria.
 *
 * NO añade endpoint: pide el MISMO `/accounting/kpis` una vez por año, en
 * paralelo. Es una llamada por ejercicio, pero la pantalla es de ADMIN y se
 * consulta de tarde en tarde; a cambio, las cifras salen del cálculo ya
 * verificado en vez de una segunda implementación que podría discrepar del
 * cuadro de mando — que es exactamente el bug de coherencia que ya costó
 * arreglar tres veces en esta contabilidad.
 *
 * Los años sin movimientos se descartan: una fila a cero no dice nada y
 * aplasta la escala del gráfico.
 */
export function YearComparison({ years }: { years: number[] }) {
  const { t } = useTranslation('accounting')
  const [rows, setRows] = useState<YearRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Ejercicios cuyo cálculo falló: se omiten del gráfico, pero se dicen. */
  const [failedYears, setFailedYears] = useState<number[]>([])

  useEffect(() => {
    const ctrl = new AbortController()
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const asc = [...years].sort((a, b) => a - b)
        const results = await Promise.all(
          asc.map(async (y) => {
            try {
              const r = await api.get<FinancialKpisResponse>(
                `/accounting/kpis?year=${y}&period=YEAR`
              )
              return { year: y, r }
            } catch {
              // Un ejercicio que falla no puede tumbar la comparativa entera...
              // pero tampoco puede desaparecer sin decir nada: una comparativa
              // a la que le falta un año, y que no avisa, se lee como si ese
              // año no hubiera existido.
              return { year: y, r: null }
            }
          })
        )
        if (ctrl.signal.aborted) return
        setFailedYears(results.filter((x) => x.r === null).map((x) => x.year))
        const mapped = results
          .filter((x): x is { year: number; r: FinancialKpisResponse } => x.r !== null)
          .map(({ year, r }) => ({
            year,
            ing: r.pnl.vars.ING,
            ebitda: r.pnl.vars.ebitda,
            net: r.pnl.vars.beneficioNeto,
            at: r.balance.vars.AT,
            pn: r.balance.vars.PN,
          }))
          .filter((r) => r.ing !== 0 || r.at !== 0)
        setRows(mapped)
      } catch (err) {
        if (ctrl.signal.aborted) return
        setError(err instanceof Error ? err.message : t('errorLoad'))
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    })()
    return () => ctrl.abort()
  }, [years, t])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm t-muted">
        <Loader2 size={16} className="animate-spin" />
        {t('loading')}
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return <p className="text-sm t-muted">{t('comparison.empty')}</p>
  }

  const avisoAnosFallidos = failedYears.length > 0 && (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <span>{t('comparison.failedYears', { years: failedYears.join(', ') })}</span>
    </div>
  )

  return (
    <section className="space-y-4">
      {avisoAnosFallidos}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <h2 className="text-sm font-semibold t-primary mb-3">{t('comparison.chartTitle')}</h2>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={4}>
            <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.4} />
            <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              width={72}
              tickFormatter={(v: number) => formatCents(v)}
            />
            <Tooltip
              cursor={{ fill: 'var(--bg-hover)' }}
              formatter={(v: number) => formatCents(v)}
              contentStyle={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontSize: 12,
              }}
            />
            <Bar dataKey="ing" name={t('comparison.income')} fill="#0ea5e9" radius={[6, 6, 0, 0]} />
            <Bar dataKey="ebitda" name="EBITDA" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
            <Bar dataKey="net" name={t('comparison.net')} fill="#16a34a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-4 mt-2">
          <LegendDot color="#0ea5e9" label={t('comparison.income')} />
          <LegendDot color="#8b5cf6" label="EBITDA" />
          <LegendDot color="#16a34a" label={t('comparison.net')} />
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border)] overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t('comparison.year')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.income')}</th>
              <th className="px-3 py-2 text-right">EBITDA</th>
              <th className="px-3 py-2 text-right">{t('comparison.net')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.assets')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.equity')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.growth')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const prev = i > 0 ? rows[i - 1]! : null
              // Sin año anterior, o con ingresos previos a cero, el crecimiento
              // no significa nada: se deja en blanco en vez de inventar un %.
              const growth =
                prev && prev.ing !== 0 ? ((r.ing - prev.ing) / Math.abs(prev.ing)) * 100 : null
              return (
                <tr key={r.year} className="border-t border-base/40">
                  <td className="px-3 py-2 font-mono t-primary">{r.year}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.ing)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.ebitda)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.net)}</td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">
                    {formatCents(r.at)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">
                    {formatCents(r.pn)}
                  </td>
                  <td
                    className="px-3 py-2 text-right font-mono"
                    style={{
                      color: growth === null ? undefined : growth >= 0 ? '#16a34a' : '#dc2626',
                    }}
                  >
                    {growth === null ? '—' : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
